/**
 * Die Suche über die Einstellungen — Seiten UND einzelne Schalter. (15.10.2026)
 *
 * Martin: *„Dass du bei den Einstellungen mir eine Art Suchfeld einbaust, damit man leichter
 * die Information findet.“* Am selben Tag fand er „Getipptes vorlesen“ nicht: der Schalter
 * liegt im Plus-Menü des Chats, nicht in den Einstellungen. Bis hierher filterte das Feld
 * nur die Zeilen der Übersicht — Titel und Stichworte der SEITEN, nie eine Einstellung.
 *
 * Der Index kommt aus EINER Quelle, die es schon gibt: shared/einstellung-aendern.ts kennt je
 * Einstellung den Titel, die Namen, unter denen Vespera sie auf Zuruf findet, und den Ort.
 * Hier wird nichts ein zweites Mal gepflegt. Ein Treffer sagt, wo er steht: führt der Ort auf
 * eine Seite, öffnet ein Tipp sie und hebt die Einstellung hervor (`hebeHervor`); lebt er
 * außerhalb (das Plus-Menü), steht der Schalter gleich in der Trefferzeile.
 *
 * Die Seitenzeilen filtert die Suche weiter wie bisher (Titel, Stichworte, sichtbarer Wert).
 */
import {
  EINSTELLUNGEN,
  norm,
  seitenOrt,
  type Eintrag,
  type Werte,
} from "../../../shared/einstellung-aendern.js";
import { EINST_SEITEN, type SeitenId } from "../../../shared/einst-seiten.js";
import { zeichen } from "../zeichen.js";
import { kontoGruppen } from "../konto-lage.js";
import { LAUTSPRECHER_SUCHE, loadAutoPlay, saveAutoPlay } from "../lautsprecher.js";
import { einstSchalter, einstText } from "./einst-bausteine.js";

/** Was die Suche von einem Eintrag braucht — die Kontowerte haben mehr (den Zuruf), die Gerätewerte genau das. */
export type SuchEintrag = Pick<Eintrag, "kennung" | "titel" | "woerter" | "ort">;

export interface Fund {
  eintrag: SuchEintrag;
  /** Die Seite, auf der die Einstellung steht — null: sie lebt außerhalb der Einstellungen. */
  seite: SeitenId | null;
  /** Ein Schalter (an/aus): außerhalb einer Seite steht er gleich in der Trefferzeile. */
  schalter: boolean;
  /** Ein Kontowert: liest und schreibt über die Einstellung selbst (shared/einstellung-aendern.ts). */
  konto?: Eintrag;
  /** Ein Gerätewert: liest und schreibt am Gerät — derselbe Weg wie die Knöpfe der App. */
  geraet?: { lese(): boolean; schreibe(an: boolean): void };
}

/**
 * Gerätewerte mit Schalter: nie in der weißen Liste des Zurufs (der Server stellt sie nicht um), aber
 * findbar. Jeder nennt sein Modul als Leser und Schreiber — keine zweite Wahrheit.
 */
const GERAETE_FUNDE: readonly Fund[] = [
  {
    eintrag: LAUTSPRECHER_SUCHE,
    seite: "stimme",
    schalter: true,
    geraet: { lese: loadAutoPlay, schreibe: saveAutoPlay },
  },
];

const seiteVon = (ort: string): SeitenId | null => EINST_SEITEN.find((s) => seitenOrt(s.id) === ort)?.id ?? null;

/*
 * NUR ANZEIGE — die gesperrten Felder. (16.10.2026, Einstellungen neu ordnen B1)
 *
 * Martin fand „Zimmermann im Chat“ nicht: die Suche kannte nur die weiße Liste des Zurufs, und die gesperrten Felder
 * (GESPERRTE_FELDER, shared/einstellung-aendern.ts) stehen dort mit Absicht nicht. Hier stehen sie NUR zum Finden: Titel und
 * Seite, sonst nichts. Ein Treffer öffnet die Seite und hebt das Feld hervor — er schaltet NIE in der Trefferzeile (`schalter`
 * ist hier immer false, es gibt kein `konto` und kein `geraet`). Die Sperrliste bleibt unberührt: diese Liste ist nicht der
 * Zuruf, sie kennt keinen Wert und schreibt nichts.
 * ⚠️ Der Titel ist der sichtbare Name auf der Seite (settings-view.ts, settings/entwickler-seite.ts) — sonst findet man unter
 *    dem Treffer etwas anderes. Ein Test hält fest, dass jede Kennung hier in GESPERRTE_FELDER steht und eine Seite hat.
 */
interface AnzeigeEintrag extends SuchEintrag {
  nurKonto1?: boolean;
  /** Suchwörter, die nur Konto 1 findet (das Zeug, das es für andere Konten nicht gibt, etwa Jev). */
  woerterKonto1?: readonly string[];
}
const nur = (
  kennung: string,
  titel: string,
  seite: SeitenId,
  woerter: readonly string[] = [],
  nurKonto1 = false,
): AnzeigeEintrag => ({ kennung, titel, woerter, ort: seitenOrt(seite), ...(nurKonto1 ? { nurKonto1 } : {}) });

export const NUR_ANZEIGE: readonly AnzeigeEintrag[] = [
  // Entwickler-Team (nur Konto 1)
  nur("entwicklerGesamt", "Entwickler", "entwickler", ["entwickler aus", "alle entwickler", "hauptschalter", "gesamtschalter", "team"], true),
  nur("entwicklerImChat", "Entwickler im Chat", "entwickler", ["entwickler", "dreier modus", "schmied", "team"], true),
  nur("zimmermannImChat", "Zimmermann im Chat", "entwickler", ["zimmermann", "zweiter entwickler"], true),
  nur("hueterImChat", "Hüter im Chat", "entwickler", ["hüter", "server 7", "sicherung", "backup"], true),
  nur("serverNetz", "Server-Netz", "entwickler", ["server", "server karte", "s6", "s7", "s8", "ampel", "platte", "updates"], true),
  nur("entwicklerVorlesen", "Entwickler vorlesen", "entwickler", ["schmied vorlesen", "entwickler stimme"], true),
  nur("zusatzVorlesen", "Zimmermann und Hüter im Gespräch vorlesen", "entwickler", ["zimmermann vorlesen", "hüter vorlesen", "automatisch vorlesen", "von selbst vorlesen"], true),
  nur("schmiedStimmeGanz", "Beiträge in einem Stück vorlesen", "entwickler", ["stream", "normal", "ganz modus", "in einem stück", "stückweise", "stück für stück", "schmied ganz", "alle entwickler"], true),
  nur("entwicklerStimme", "Stimme des Schmieds", "entwickler", ["stimme des entwicklers", "schmied stimme"], true),
  nur("zimmermannStimme", "Stimme des Zimmermanns", "entwickler", ["zimmermann stimme"], true),
  nur("hueterStimme", "Stimme des Hüters", "entwickler", ["hüter stimme"], true),
  nur("entwicklerTempo", "Sprechtempo des Schmieds", "entwickler", ["schmied tempo", "schmied schneller", "schmied langsamer", "sprechgeschwindigkeit", "geschwindigkeit"], true),
  nur("zimmermannTempo", "Sprechtempo des Zimmermanns", "entwickler", ["zimmermann tempo", "zimmermann schneller", "zimmermann langsamer", "sprechgeschwindigkeit", "geschwindigkeit"], true),
  nur("hueterTempo", "Sprechtempo des Hüters", "entwickler", ["hüter tempo", "hüter schneller", "hüter langsamer", "sprechgeschwindigkeit", "geschwindigkeit"], true),
  nur("entwicklerAusfuehrlichkeit", "Ausführlichkeit des Schmieds", "entwickler", ["schmied kurz", "schmied ausführlich", "wie viel schreibt der schmied"], true),
  nur("zimmermannAusfuehrlichkeit", "Ausführlichkeit des Zimmermanns", "entwickler", ["zimmermann kurz", "zimmermann ausführlich", "wie viel schreibt der zimmermann"], true),
  nur("hueterAusfuehrlichkeit", "Ausführlichkeit des Hüters", "entwickler", ["hüter kurz", "hüter ausführlich", "wie viel schreibt der hüter"], true),
  // Sprechtaste und „Hey Vespera“
  nur("wachwort", "Auf „Hey Vespera“ hören", "sprechen", ["wachwort", "wach wort", "hey vespera"]),
  nur("wachwortNurMeineStimme", "Hey Vespera nur mit meiner Stimme", "sprechen", ["nur meine stimme"]),
  { ...nur("autoMikroNachFrage", "Mikro von selbst öffnen", "sprechen", ["auto mikro", "mikro nach frage"]), woerterKonto1: ["jev entscheidet"] },
  nur("nachlauschMs", "Kurz zuhören", "sprechen", ["nachlauschen", "mikro geht wieder zu"]),
  nur("mikroVorstart", "Mikro kurz vor dem Ende öffnen", "sprechen", ["vorstart", "mikro früher", "satzanfang fehlt"]),
  nur("bereitTon", "Leiser Ton, wenn das Mikro bereit ist", "sprechen", ["bereit ton", "piep", "signalton"]),
  nur("sendeWort", "Auslösewort", "sprechen", ["sendewort", "jetzt senden"]),
  nur("warteSatz", "Kurz Bescheid geben, wenn die Antwort dauert", "sprechen", ["warte satz", "einen moment"], true),
  // Mitteilungen, Stimmprobe
  nur("antwortVorschauSperrbildschirm", "Antwortvorschau auf dem Sperrbildschirm", "mitteilungen", ["sperrbildschirm"]),
  nur("stimmungAusStimme", "Stimmung aus der Stimme", "stimmprobe", ["stimmung"]),
  // Erweitert (Versuche)
  nur("mikroOffenBeimVorlesen", "Mikro beim Vorlesen offen halten (Test)", "versuche", ["mikro offen", "versuch"]),
  nur("ttsDirektBeta", "Vorlesen sofort", "versuche", ["versuch", "beta"], true),
  nur("ttsPcmBeta", "Direkt aus dem Datenstrom spielen", "versuche", ["schneller ton", "datenstrom", "pcm"], true),
  nur("ttsTempoNativ", "Tempo von der Stimme selbst", "versuche", ["schneller ton", "tempo nativ"], true),
  nur("ttsStilleKuerzen", "Pausen kürzen", "versuche", ["schneller ton", "pausen"], true),
  nur("vorantwortBeta", "Sofort-Antwort bei Aufträgen", "versuche", ["sofort antwort", "vorantwort"], true),
];

/** Der Index: jede Einstellung der weißen Liste, die dieses Konto sehen darf, die Gerätewerte und die nur angezeigten Felder. */
export function suchIndex(konto1: boolean): Fund[] {
  const konto: Fund[] = EINSTELLUNGEN.filter((e) => konto1 || !e.nurKonto1).map((e) => ({
    eintrag: e,
    seite: seiteVon(e.ort),
    // Die Vorgabe eines Schalters ist ein Wahrheitswert — das sagt die Einstellung selbst.
    schalter: typeof e.lese({}) === "boolean",
    konto: e,
  }));
  const anzeige: Fund[] = NUR_ANZEIGE.filter((e) => konto1 || !e.nurKonto1).map((e) => ({
    eintrag: { kennung: e.kennung, titel: e.titel, woerter: konto1 ? [...e.woerter, ...(e.woerterKonto1 ?? [])] : e.woerter, ort: e.ort },
    seite: seiteVon(e.ort),
    // NIE ein Schalter in der Trefferzeile (oben).
    schalter: false,
  }));
  // Die Seite „Erweitert“ (Versuche) gibt es nur im Hauptkonto — auch ihre Einstellungen sind dort nicht zu finden.
  return [...konto, ...GERAETE_FUNDE, ...anzeige].filter((f) => konto1 || f.seite !== "versuche");
}

/** Die Treffer zu einem Suchwort: Titel, ein Name oder der Ort enthält es (Schreibweise wie beim Zuruf: `norm`). */
export function findeEinstellungen(index: readonly Fund[], wort: string): Fund[] {
  const q = norm(wort);
  if (q === "") return [];
  const imTitel = (f: Fund): boolean => norm(f.eintrag.titel).includes(q);
  const treffer = index.filter((f) => imTitel(f) || [...f.eintrag.woerter, f.eintrag.ort].some((n) => norm(n).includes(q)));
  // Was das Wort im Namen trägt, zuerst; dahinter die Treffer über Namen des Zurufs und den Ort (Reihenfolge bleibt).
  return [...treffer.filter(imTitel), ...treffer.filter((f) => !imTitel(f))];
}

/** Wo der Treffer steht, als Satz unter dem Namen. */
export function fundOrt(f: Fund): string {
  if (f.seite) return EINST_SEITEN.find((s) => s.id === f.seite)?.titel ?? f.eintrag.ort;
  const ort = f.eintrag.ort;
  const gross = ort.charAt(0).toUpperCase() + ort.slice(1);
  return f.schalter ? `${gross} — hier umschaltbar` : gross;
}

/**
 * Die Einstellung auf der geöffneten Seite hervorheben: die Zeile mit dem Feldnamen (`data-feld`)
 * oder dem Namen des Schalters. Nicht jede Seite kennt den Namen — dann bleibt es beim Öffnen.
 */
export function hebeHervor(wurzel: ParentNode, e: SuchEintrag): HTMLElement | null {
  const name = e.titel.replace(/[„“"]/g, "");
  const ziel = wurzel.querySelector<HTMLElement>(
    `.einst-inhalt [data-feld="${e.kennung}"], .einst-inhalt [aria-label="${name}"]`,
  );
  if (!ziel) return null;
  // 16.10.2026: steht die Einstellung in einem eingeklappten Block (Seite „Erweitert“), geht er auf — sonst sähe man nichts.
  const block = ziel.closest("details");
  if (block) block.open = true;
  const zeile = ziel.closest<HTMLElement>(".einst-feld, .einst-feld-block, .einst-team-zeile, .settings-field") ?? ziel;
  zeile.classList.add("einst-fund-ziel");
  zeile.addEventListener("animationend", () => zeile.classList.remove("einst-fund-ziel"), { once: true });
  try {
    zeile.scrollIntoView({ block: "center" });
  } catch {
    /* ohne Layout (Tests) */
  }
  return zeile;
}

export interface SucheKontext {
  index: readonly Fund[];
  /** Der Stand der Einstellungen — für den Schalter in der Trefferzeile. */
  werte: () => Werte;
  /** Die Zeilen der Seiten, ihre Gruppen und „Gerade“ — sie werden wie bisher gefiltert. */
  seitenZeilen: readonly HTMLElement[];
  abschnitte: readonly HTMLElement[];
  gerade: HTMLElement;
  /** Weitere Blöcke über der Übersicht (die Lage-Karte) — sie weichen der Suche wie „Gerade“. */
  weitere?: readonly (HTMLElement | null)[];
  oeffne: (f: Fund) => void;
  schalte: (f: Fund, an: boolean, zuruecksetzen: () => void) => void;
}

export interface SucheTeile {
  feld: HTMLElement;
  eingabe: HTMLInputElement;
  funde: HTMLElement;
  leer: HTMLElement;
}

/** Das Suchfeld, der Block der Treffer und die Leerzeile — die Übersicht hängt sie an ihren Platz. */
export function baueSuche(k: SucheKontext): SucheTeile {
  const feld = document.createElement("label");
  feld.className = "einst-suche";
  const lupe = document.createElement("span");
  lupe.className = "einst-suche-lupe";
  lupe.innerHTML = zeichen("suche", 18);
  const eingabe = document.createElement("input");
  eingabe.type = "search";
  eingabe.className = "einst-suche-feld";
  eingabe.placeholder = "Einstellung suchen";
  eingabe.setAttribute("aria-label", `Einstellung suchen — zum Beispiel Stimme, ${kontoGruppen().anrufModell ? "Anruf" : "Mikro"} oder Getipptes vorlesen`);
  eingabe.setAttribute("enterkeyhint", "search");
  eingabe.autocomplete = "off";
  feld.append(lupe, eingabe);

  const funde = document.createElement("section");
  funde.className = "einst-funde";
  funde.setAttribute("aria-label", "Gefundene Einstellungen");
  funde.hidden = true;
  const stand = document.createElement("h3");
  stand.className = "einst-gruppe-titel";
  stand.setAttribute("aria-live", "polite");
  const karte = document.createElement("div");
  karte.className = "einst-karte";

  const leer = document.createElement("p");
  leer.className = "einst-leer";
  leer.setAttribute("role", "status");
  leer.hidden = true;

  const zeile = (f: Fund): HTMLElement => {
    const e = f.eintrag;
    // Nur ein Wert der weißen Liste (`konto`) oder ein Gerätewert schaltet in der Trefferzeile — nie ein nur angezeigter.
    if (!f.seite && f.schalter && (f.konto || f.geraet)) {
      const an = f.konto ? f.konto.lese(k.werte()) === true : (f.geraet?.lese() ?? false);
      const s = einstSchalter(e.titel, fundOrt(f), an, (neu, zurueck) => k.schalte(f, neu, zurueck), e.kennung);
      s.zeile.classList.add("einst-fund-schalter");
      s.zeile.dataset.kennung = e.kennung;
      return s.zeile;
    }
    const b = document.createElement("button");
    b.type = "button";
    b.className = "einst-fund";
    b.dataset.kennung = e.kennung;
    const pfeil = document.createElement("span");
    pfeil.className = "einst-zeile-pfeil";
    pfeil.innerHTML = zeichen("pfeil-rechts", 16);
    b.append(einstText(e.titel, fundOrt(f)), pfeil);
    b.addEventListener("click", () => k.oeffne(f));
    return b;
  };

  const suche = (): void => {
    const roh = eingabe.value.trim();
    const q = roh.toLowerCase();
    let seiten = 0;
    for (const z of k.seitenZeilen) {
      const text = `${z.dataset.suche ?? ""} ${z.textContent ?? ""}`.toLowerCase();
      const passt = q === "" || text.includes(q);
      z.hidden = !passt;
      if (passt) seiten++;
    }
    for (const a of k.abschnitte) a.hidden = a.querySelector(".einst-zeile:not([hidden])") === null;
    k.gerade.hidden = q !== "" || k.gerade.dataset.leer === "1";
    for (const w of k.weitere ?? []) if (w) w.hidden = q !== "";

    const treffer = findeEinstellungen(k.index, roh);
    karte.replaceChildren(...treffer.map(zeile));
    stand.textContent = treffer.length === 1 ? "1 Einstellung gefunden" : `${treffer.length} Einstellungen gefunden`;
    // Die Überschrift hängt erst, wenn es Treffer gibt — die Übersicht zählt ihre Gruppentitel.
    funde.replaceChildren(...(treffer.length ? [stand, karte] : []));
    funde.hidden = treffer.length === 0;

    leer.hidden = q === "" || seiten > 0 || treffer.length > 0;
    leer.textContent = `Keine Einstellung passt zu „${roh}“. Versuch ein anderes Wort — etwa „Stimme“, ${kontoGruppen().anrufModell ? "„Anruf“ oder „Mikro“" : "„Mikro“ oder „Verlauf“"}.`;
  };
  eingabe.addEventListener("input", suche);
  eingabe.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && eingabe.value !== "") {
      ev.preventDefault();
      eingabe.value = "";
      suche();
      return;
    }
    if (ev.key === "Enter" && eingabe.value.trim() !== "") {
      // Enter öffnet den ersten Treffer — eine Einstellung vor einer Seite.
      const erster =
        funde.querySelector<HTMLElement>(".einst-fund") ?? k.seitenZeilen.find((z) => !z.hidden) ?? null;
      if (erster) {
        ev.preventDefault();
        erster.click();
      }
    }
  });
  return { feld, eingabe, funde, leer };
}
